import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { jobsAPI } from '../lib/api';
import { Search, Briefcase, MapPin, Clock, X, DollarSign, Building } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const JobsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [jobType, setJobType] = useState(searchParams.get('type') || 'all');
  const [experienceLevel, setExperienceLevel] = useState(searchParams.get('level') || 'all');

  const jobTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'full-time', label: 'Full-time' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' },
  ];

  const experienceLevels = [
    { value: 'all', label: 'All Levels' },
    { value: 'entry', label: 'Entry Level' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior' },
    { value: 'lead', label: 'Lead / Manager' },
  ];

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        const params = {};
        if (search) params.search = search;
        if (jobType && jobType !== 'all') params.job_type = jobType;
        if (experienceLevel && experienceLevel !== 'all') params.experience_level = experienceLevel;
        const res = await jobsAPI.getAll(params);
        setJobs(res.data);
      } catch (err) {
        console.error('Error fetching jobs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [search, jobType, experienceLevel]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (jobType && jobType !== 'all') params.set('type', jobType);
    if (experienceLevel && experienceLevel !== 'all') params.set('level', experienceLevel);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setJobType('all');
    setExperienceLevel('all');
    setSearchParams({});
  };

  const hasFilters = search || (jobType && jobType !== 'all') || (experienceLevel && experienceLevel !== 'all');

  return (
    <div data-testid="jobs-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Careers</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Job Board</h1>
          <p className="text-muted-foreground max-w-2xl">
            Find your next opportunity in Portland's tech ecosystem. Browse open 
            positions at startups, established companies, and growing tech teams.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border sticky top-16 z-40 bg-background">
        <div className="container-wide py-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search jobs or companies..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="jobs-search"
              />
            </div>
            <Select value={jobType} onValueChange={setJobType}>
              <SelectTrigger className="w-[150px]" data-testid="job-type-select">
                <SelectValue placeholder="Job Type" />
              </SelectTrigger>
              <SelectContent>
                {jobTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={experienceLevel} onValueChange={setExperienceLevel}>
              <SelectTrigger className="w-[150px]" data-testid="experience-select">
                <SelectValue placeholder="Experience" />
              </SelectTrigger>
              <SelectContent>
                {experienceLevels.map(level => (
                  <SelectItem key={level.value} value={level.value}>
                    {level.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="search-btn">Search</Button>
            {hasFilters && (
              <Button type="button" variant="ghost" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </form>
        </div>
      </section>

      {/* Results */}
      <section>
        <div className="container-wide py-12">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="border border-border p-6 animate-pulse">
                  <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                  <div className="h-6 bg-muted rounded w-1/2 mb-2" />
                  <div className="h-3 bg-muted rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : jobs.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground mb-6">
                {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'} found
              </p>
              <div className="space-y-4">
                {jobs.map((job, index) => (
                  <Link
                    key={job.id}
                    to={`/jobs/${job.id}`}
                    className="block animate-slide-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`job-card-${job.id}`}
                  >
                    <Card className="card-hover">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center">
                                <Building className="w-5 h-5 text-muted-foreground" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-lg">{job.title}</h3>
                                <p className="text-sm text-muted-foreground">{job.company}</p>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-4 md:mb-0">
                              {job.description}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2 md:flex-col md:items-end md:gap-1">
                            <span className="badge-category">{job.job_type}</span>
                            <span className="text-xs text-muted-foreground capitalize">
                              {job.experience_level} level
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location}
                            {job.is_remote && ' (Remote)'}
                          </span>
                          {job.salary_range && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {job.salary_range}
                            </span>
                          )}
                          {job.created_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(job.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <Briefcase className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-2">No jobs found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search or filters
              </p>
              <Button variant="outline" onClick={clearFilters}>
                Clear all filters
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
